import { db, auth, getSiteKey } from "../firebaseConfig";
import { doc, collection, getDocs, getDoc, updateDoc, query, where } from "firebase/firestore";
import { ConfirmSnapScanPaymentStatus, FireStoreOrder, Order, PaymentType } from "../model/OrderModel";
import { CartItem } from "../model/CartModel";
import { Utils } from "../common/Utils";
import { MessageType } from "./MessageGateway";

interface SiteSettings {
    vatPercentage: number;
    deliveryCost: number;
}

export interface OrderResponse {
    orderId: string;
    orderNumber: string;
    status: string;
}

export const OrderGateway = {

    SaveOrder: async (cart: CartItem[], deliveryLocation: string, firstName: string, lastName: string, email: string, phoneCode: string, phoneNumber: string, comments: string, settings: SiteSettings, messagePreference: MessageType, paymentMethod: PaymentType, orderId: string | null) => {

        const user = auth.currentUser;
        const vatPercentage = settings.vatPercentage ? Number(settings.vatPercentage) / 100 : 0;
        // Format cart items with VAT
        const cartItems = cart.map(item => ({
            id: item.id,
            name: item.name,
            quantity: item.quantity,
            priceAfterTax: Number(item.price).toFixed(2),
            priceBeforeTax: (Number(item.price) / (1 + vatPercentage)).toFixed(2), // Adding VAT
            attributes: item.attributes,
            imageUrl: item.imageUrl || "",
        }));

        const deliveryCost = deliveryLocation === "" ? 0 : Number(settings.deliveryCost);
        const subtotal = cart.reduce((sum, item) => sum + item.quantity * Number(item.price), 0);
        const totalAmount = subtotal + deliveryCost;
        const vatAmount = totalAmount - totalAmount / (1 + vatPercentage);

        // Define order data
        const orderData: Order = {
            orderNumber: "tbc",
            userId: user ? user.uid : null,
            firstName: firstName,
            lastName: lastName,
            email: user ? user.email : email,
            phoneNumber: Utils.FormatPhoneNumber(phoneCode, phoneNumber),
            cartItems,
            subtotal: subtotal.toFixed(2),
            vatAmount: vatAmount.toFixed(2),
            deliveryFee: deliveryCost.toFixed(2),
            totalAmount: totalAmount.toFixed(2),
            deliveryLocation,
            comments,
            createdAt: new Date().toISOString(),
            status: "",
            messagePreference,
            paymentMethod,
            finalPaymentMethod: "",
            totalPaid: "0.00",
        };

        const headers = {
            "Content-Type": "application/json",
            "x-client-key": getSiteKey(),
            Authorization: ""
        };

        if (user) {
            headers.Authorization = `Bearer ${await user?.getIdToken()}`;
        }

        const response = await fetch(`${process.env.REACT_APP_MESSAGE_API_HOST}/order`, {
            method: "POST",
            headers,
            body: JSON.stringify({ order: orderData, orderId })
        });

        if (!response.ok) {
            const errorData = await response.json();
            console.error(`Error calling save order. (Status code: ${response.status})`, errorData)
            throw new Error(errorData.error || "Failed call order save");
        }

        return response;
    },

    GetOrders: async (userId: string | null) => {
        const ordersRef = collection(db, "orders");
        const ordersSnapshot = await getDocs(ordersRef);
        return ordersSnapshot.docs
            .filter(doc => !userId || doc.data().userId === userId)
            .map(doc => ({
                id: doc.id,
                order: doc.data(),
            })) as FireStoreOrder[];
    },

    GetOrdersForAnalytics: async (effectiveDate: Date) => {
        const ordersRef = collection(db, "orders");
        const ordersSnapshot = await getDocs(ordersRef);
        const oneYearAgo = new Date(effectiveDate.toDateString());
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        return ordersSnapshot.docs
            .filter(doc => {
                const data = doc.data();
                const createdAt = new Date(data.createdAt);
                return (
                    data.status === "Completed" &&
                    createdAt > oneYearAgo &&
                    createdAt <= effectiveDate
                );
            })
            .map(doc => ({
                id: doc.id,
                order: doc.data(),
            })) as FireStoreOrder[];
    },    

    GetOrder: async (orderId: string) => {
        const ref = doc(db, "orders", orderId);
        const docSnap = await getDoc(ref);

        if (docSnap.exists()) {
            return docSnap.data() as Order;
        }

        return null;
    },
    UpdateOrder: async (orderId: string, status: string) => {
        const orderRef = doc(db, "orders", orderId);
        return updateDoc(orderRef, { status, updatedAt: new Date().toISOString() });
    },
    UpdateOrderByOrderNumber: async (orderNumber: string, status: string) => {
        const ordersRef = collection(db, "orders");
        const q = query(ordersRef, where("orderNumber", "==", orderNumber));

        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const docRef = querySnapshot.docs[0].ref;

            await updateDoc(docRef, {
                status
            });
        }
    },
    GetSnapScanOrderSummary: async (orderId: string, phoneCode: string, phoneNumber: string, email: string) : Promise<ConfirmSnapScanPaymentStatus | null>  => {
        const user = auth.currentUser;
        const headers = {
            "Content-Type": "application/json",
            "x-client-key": getSiteKey(),
            Authorization: ""
        };

        if (user) {
            headers.Authorization = `Bearer ${await user?.getIdToken()}`;
        }

        const response = await fetch(`${process.env.REACT_APP_MESSAGE_API_HOST}/confirmSnapScanPayment`, {
            method: "POST",
            headers,
            body: JSON.stringify({ orderId, phoneNumber: phoneNumber ? Utils.FormatPhoneNumber(phoneCode, phoneNumber) : null, email })
        });

        if (response.ok)
        {
            return await response.json() as ConfirmSnapScanPaymentStatus;
        }
        
        return null;
    }
}
